import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, App, Button, DatePicker, Drawer, Dropdown, Form, Input, Modal, Segmented, Select, Space, Switch, Table, Tabs, Tag, Typography, Upload
} from 'antd';
import {
    CheckCircleFilled, CloseCircleFilled, DeleteOutlined, DownloadOutlined, EllipsisOutlined, ExportOutlined, InboxOutlined, LoadingOutlined,
    SafetyOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { downloadName } from '@lfoc/domain/intake/batch.js';

import StatusTag from '../../components/StatusTag.jsx';
import { STATUS_KIND, statusLabel } from '../../status/statusHelp.js';

import { useAccess } from '../../auth/AccessContext.jsx';
import ActionIcon from '../../components/ActionIcon.jsx';
import { useLiveTopic } from '../../live/LiveContext.js';
import { downloadCsv } from '../../domain/redemptionDocument.js';
import { StepUpCancelled, useStepUp } from '../../stepup/useStepUp.js';
import { ClassSummary, WarningsBlock } from './BatchSummary.jsx';
import ErrorLog from './ErrorLog.jsx';
import HoldingChecks from './HoldingChecks.jsx';
import { appendRows, rowsQuery, showsHoldingChecks, withheldText } from './batchDetail.js';
import { appendBatches, byNumber, byText, filterOptions, rowNote, toListRows } from './batchList.js';
import { useColumnSearch } from './columnSearch.jsx';
import { findingLine, toErrorRows, unshownFindings } from './errorRows.js';
import { templateFor, templateGroups } from './templates.js';
import {
    MAX_STAGED, ROW_STATUS, classOptions, dateForAll, listSummary, rowProblem, settleRow, sizeText, stageFiles, toSend, uploadFundIds
} from './stagedFiles.js';
import {
    NOT_STORED, SCAN_LAPSE_MINUTES, colomboToday, createScanWatch, decisionErrorMessage, downloadNotice, downloadedBytes,
    isStaleDecision, needsAccounts, postToStore, prepareUpload, stateNotice, storeErrorMessage, uploadErrorMessage, verdictNotice
} from './uploadFile.js';
import {
    KIND_FILTERS, batchesQuery, classLabelOf, dateAllowed, dateRule, kindOptions, mayDecide, takesLoadKind
} from './uploadKinds.js';

const { Dragger } = Upload;
const { Text } = Typography;

const STATE_COLOUR = Object.freeze({
    RECEIVED: 'default',
    SCANNING: 'processing',
    VALIDATED: 'blue',
    QUARANTINED: 'orange',
    REJECTED_MALWARE: 'red',
    SCAN_FAILED: 'red',
    ACCEPTED: 'cyan',
    PROCESSED: 'green',
    REJECTED: 'default',
    SUPERSEDED: 'default',
    ACCEPTANCE_EXPIRED: 'default'
});

const at = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { timeZone: 'Asia/Colombo', dateStyle: 'medium', timeStyle: 'short' }) : '—');

/**
 * File Uploads (spec 8.1, IP-60; IP-91 to IP-96), on the Console API.
 *
 * Uploading, accepting and rejecting a file each ask for a fresh authenticator
 * code (IP-76, IP-96), and the person may cancel at any point with nothing
 * sent:
 *
 *   - Upload: the code is bound to the file's six facts (its SHA-256 among
 *     them). The API answers with a ticket - the batch, already SCANNING, and
 *     a presigned form - and the browser posts the bytes straight to the
 *     intake bucket (uploadFile.js postToStore). GuardDuty scans them and the
 *     verdict's consumer re-hashes and parses them; this screen polls the
 *     SCANNING list until the verdict lands (createScanWatch), for at most
 *     3 minutes.
 *   - Accept (another person) and reject: the code is bound to the batch and
 *     the bytes the checker was shown. The server refuses the uploader, and an
 *     accept its downstream step would refuse, before any code is asked for.
 *
 * A form never posted, or refused by the store, leaves its batch SCANNING: it
 * lapses to SCAN_FAILED 30 minutes after the upload (IP-93). The drawer says
 * what each state means (uploadFile.js stateNotice), and an accept or reject
 * refused because the batch moved on reloads it (isStaleDecision).
 *
 * Several files may be chosen at once (up to MAX_STAGED): each becomes a row
 * under the drop zone (stagedFiles.js) - its class (guessed from its name),
 * kind, date and, for a statement, account, all editable in place - and
 * Proceed sends them on ONE code: a single file as INTAKE_UPLOAD, several as
 * INTAKE_UPLOAD_BATCH (POST intake/upload-batches), whose code is bound to
 * every file's facts and whose ticket opens every batch or none - a refusal
 * naming a file (details.fileIndex) is said on that row. Each file's bytes
 * then go on its own form. A row's Progress is Sending, a tick once the
 * bucket took its bytes, or what refused it - a refused row is sent again on
 * the next Proceed. Closing the code dialog sends nothing. The scan's verdict
 * is the Uploaded files list's to show.
 *
 * The list names each uploader; its Error Log tab gathers every file that
 * could not be used, in the words the drawer uses. A file the scan cleared can
 * be downloaded again, exactly as uploaded, from its row's menu - offered by
 * the server's own test (uploadFile.js downloadNotice), saved only once its
 * SHA-256 is the batch's (downloadedBytes), and under batch.js downloadName -
 * safe characters and one .csv - never the name its uploader gave (C-32).
 *
 * The collection accounts load once a statement is listed; a failed load is
 * said above the list, with Try again, and is retried whenever files are
 * chosen (uploadFile.js needsAccounts).
 *
 * IP-103 (design 2026-10-02 sections 3, 4 and 8): six classes - NAV values,
 * bank statements, and the Core's own calendar, unit prices, holdings and
 * transactions, each of those four a Daily file or an Initial load (the
 * kind; Initial is offered only with core.intake.initial). The date follows
 * the kind (uploadKinds.js dateRule): a daily file is for today in Colombo
 * and its date is locked; an initial load's "As-at date" is before today; a
 * NAV file's and a statement's is today or earlier, as before. LIV's file
 * names choose the class (and a daily-* name the Daily kind:
 * uploadFile.js guessUpload), and "Download template" gives the header row
 * of each class and kind (templates.js). The upload's code is bound to the
 * kind too (loadKind in INTAKE_UPLOAD's params).
 *
 * The list has a Kind column and filter (GET intake/batches?loadKind=), a page
 * of 100 at a time - "Load more" follows the server's cursor (?cursor=). The
 * drawer shows the class's own summary and a VALIDATED file's warnings, its
 * rows a page at a time - every row, or with "Rows with findings only" just
 * those (?withFindings=true) - following the server's cursor, says so when
 * the rows are client data the reader may not see (rowsWithheld), and for
 * an accepted daily holdings file lists the breaks against the register
 * (HoldingChecks). Accepting or rejecting an initial load also needs
 * core.intake.initial (uploadKinds.js mayDecide); the server judges the
 * rest (LOAD_NOT_PERMITTED names the claim).
 */
const UploadsTab = () => {
    const { api, canAct, principal } = useAccess();
    const { message } = App.useApp();
    const { withStepUp, stepUpDialog } = useStepUp();
    const canUpload = canAct('uploads', 'upload');
    const canAccept = canAct('uploads', 'accept');
    const canReject = canAct('uploads', 'reject');
    // IP-103: the Initial load kind - to upload one, and alongside accept's own claim to accept or reject one.
    const canInitial = canAct('uploads', 'initial');
    const me = principal?.uid;

    // The files chosen, a row each (stagedFiles.js), and those a drop left out, with why.
    const [staged, setStaged] = useState([]);
    const stagedRef = useRef(staged);
    stagedRef.current = staged;
    const [skipped, setSkipped] = useState([]);
    // Bumped by each pick or drop, so a failed accounts load is asked again.
    const [picks, setPicks] = useState(0);
    const hasStatement = staged.some((r) => r.fileClass === 'BANK_STATEMENT');
    const [accounts, setAccounts] = useState([]);
    // The accounts' own load: its failure is said above the list, and Try again bumps the attempt.
    const [accountsError, setAccountsError] = useState(null);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [accountsAttempt, setAccountsAttempt] = useState(0);
    // The funds the File category offers a security holdings and a fund facts class for (GET register/funds, uploadFundIds).
    const [fundIds, setFundIds] = useState([]);
    const [fundsError, setFundsError] = useState(null);
    const [fundsAttempt, setFundsAttempt] = useState(0);
    const categoryOptions = useMemo(() => classOptions(fundIds, canInitial), [fundIds, canInitial]);
    const [sending, setSending] = useState(false);

    const [batches, setBatches] = useState([]);
    // The cursor of the list's next page (GET intake/batches?cursor=), null when every batch is listed.
    const [nextCursor, setNextCursor] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    // The list's Kind filter (KIND_FILTERS): ALL, DAILY or INITIAL.
    const [kindFilter, setKindFilter] = useState('ALL');
    const [loading, setLoading] = useState(false);
    const [refreshedAt, setRefreshedAt] = useState(null);
    const [listError, setListError] = useState(null);
    const [openBatchId, setOpenBatchId] = useState(null);
    // The row whose file is being downloaded (its menu button spins).
    const [downloadingId, setDownloadingId] = useState(null);
    // Bumped when a verdict lands for the batch the drawer shows, so it reloads.
    const [drawerVersion, setDrawerVersion] = useState(0);

    // The one SCANNING poll for this screen. Its callbacks read the latest
    // render through a ref, so the poll lives as long as the screen and stops
    // when it unmounts.
    const notify = useRef(null);
    notify.current = {
        settled: async (batchId) => {
            try {
                const { batch } = await api.intake.batch(batchId, { limit: 1 });
                setBatches((current) => current.map((b) => (b.batchId === batch.batchId ? { ...b, ...batch } : b)));
                if (openBatchId === batch.batchId) setDrawerVersion((v) => v + 1);
                const { type, text } = verdictNotice(batch);
                message[type](text, 8);
            } catch {
                // The list shows the verdict at the next refresh.
            }
        },
        gaveUp: (batchId) => {
            const name = batches.find((b) => b.batchId === batchId)?.fileName ?? 'A file you uploaded';
            message.info(name + ' is still being scanned. Press Refresh to check again; with no verdict it shows SCAN_FAILED '
                + SCAN_LAPSE_MINUTES + ' minutes after the upload.', 8);
        }
    };
    const watch = useRef(null);
    useEffect(() => {
        const scans = createScanWatch({
            listScanning: async () => (await api.intake.batches({ state: 'SCANNING', limit: 200 })).batches.map((b) => b.batchId),
            onSettled: (batchId) => notify.current.settled(batchId),
            onGaveUp: (batchId) => notify.current.gaveUp(batchId)
        });
        watch.current = scans;
        return () => scans.stop();
    }, [api]);

    const load = useCallback(async () => {
        setLoading(true);
        setListError(null);
        try {
            const { batches: list, nextCursor: next } = await api.intake.batches(batchesQuery(kindFilter));
            setBatches(list);
            setNextCursor(next ?? null);
            setRefreshedAt(new Date());
        } catch (e) {
            setListError(e.message);
        } finally {
            setLoading(false);
        }
    }, [api, kindFilter]);
    // Live screens (docs/live-sync/implementation-plan.md 4.6): re-read, exactly as Refresh does, when
    // a file was uploaded, scanned, accepted or rejected - by anyone, or by the system's price-file accept.
    useLiveTopic(['uploads'], load);

    useEffect(() => {
        load();
    }, [load]);

    /** Load more: the next page of the list, same kind, under the batches shown. */
    const loadMore = async () => {
        setLoadingMore(true);
        setListError(null);
        try {
            const page = await api.intake.batches(batchesQuery(kindFilter, undefined, nextCursor));
            setBatches((current) => appendBatches(current, page.batches));
            setNextCursor(page.nextCursor ?? null);
        } catch (e) {
            setListError(e.message);
        } finally {
            setLoadingMore(false);
        }
    };

    const errorRows = useMemo(() => toErrorRows(batches), [batches]);
    // The Uploaded files list: every column sorts; category and status filter by a tick list, the rest search (batchList.js).
    const listRows = useMemo(() => toListRows(batches), [batches]);
    const { textSearch, dateTimeRangeSearch, dateRangeSearch } = useColumnSearch();

    // Each pick or drop and each Try again asks again while none are loaded.
    useEffect(() => {
        if (!needsAccounts({ open: hasStatement, fileClass: 'BANK_STATEMENT', loaded: accounts.length })) return undefined;
        let live = true;
        setAccountsError(null);
        setAccountsLoading(true);
        api.intake.accounts()
            .then(({ accounts: list }) => { if (live) setAccounts(list); })
            .catch((e) => { if (live) setAccountsError(e.message); })
            .finally(() => { if (live) setAccountsLoading(false); });
        return () => { live = false; };
    }, [api, hasStatement, picks, accounts.length, accountsAttempt]);

    // Once, for one who may upload; Try again asks again.
    useEffect(() => {
        if (!canUpload) return undefined;
        let live = true;
        setFundsError(null);
        api.register.funds()
            .then(({ funds }) => { if (live) setFundIds(uploadFundIds(funds)); })
            .catch((e) => { if (live) setFundsError(e.message); });
        return () => { live = false; };
    }, [api, canUpload, fundsAttempt]);

    const settleOpts = () => ({ canInitial, today: colomboToday() });
    const patchRow = (key, patch) => setStaged((current) => current.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    /** A row's class, kind, date or account changed by hand. */
    const editRow = (key, changed) => setStaged((current) => current.map((r) => (r.key === key ? settleRow(r, changed, settleOpts()) : r)));
    const removeRow = (key) => setStaged((current) => current.filter((r) => r.key !== key));

    /** One pick or drop: the files become rows, and those left out are said. */
    const choose = (files) => {
        const { rows, skipped: left } = stageFiles(stagedRef.current, files, settleOpts());
        setStaged(rows);
        setSkipped(left);
        setPicks((n) => n + 1);
    };

    /** A row's facts and bytes as the upload's code is bound to them (prepareUpload), or why they cannot be sent. */
    const prepareRow = async (row) => prepareUpload({
        bytes: await row.file.arrayBuffer(),
        fileName: row.file.name,
        fileClass: row.fileClass,
        businessDate: row.businessDate,
        accountId: row.accountId,
        loadKind: row.loadKind,
        today: colomboToday()
    });

    /** A ticket issued: its batch heads the list, then the bytes go on its form. True once the bucket took them. */
    const postRow = async (row, prepared, { batch, upload: form }) => {
        setBatches((current) => [{ ...batch, uploadedByName: principal?.displayName ?? null }, ...current.filter((b) => b.batchId !== batch.batchId)]);
        const stored = await postToStore({ ...form, bytes: prepared.bytes, fileName: prepared.params.fileName });
        if (!stored.ok) {
            // The ticket is spent: sending it again uploads it afresh, once its batch has lapsed.
            patchRow(row.key, { status: ROW_STATUS.ERROR, error: storeErrorMessage(stored.code, stored.message) + ' ' + NOT_STORED });
            return false;
        }
        patchRow(row.key, { status: ROW_STATUS.DONE });
        watch.current?.watch(batch.batchId);
        return true;
    };

    /** A refusal's words on its row: the server names the file first (refusalForFile), which the row already shows. */
    const rowError = (e, row) => {
        const text = uploadErrorMessage(e);
        return text.startsWith(row.file.name + ': ') ? text.slice(row.file.name.length + 2) : text;
    };

    /** One file: INTAKE_UPLOAD, its own code. */
    const sendOne = async (row) => {
        patchRow(row.key, { status: ROW_STATUS.SENDING, error: null });
        try {
            const prepared = await prepareRow(row);
            if (prepared.error) {
                patchRow(row.key, { status: ROW_STATUS.ERROR, error: prepared.error });
                return 0;
            }
            const ticket = await withStepUp('INTAKE_UPLOAD', prepared.params, (stepUp) => api.intake.upload({ ...prepared.params, stepUp }));
            return (await postRow(row, prepared, ticket)) ? 1 : 0;
        } catch (e) {
            patchRow(row.key, e instanceof StepUpCancelled ? { status: ROW_STATUS.READY } : { status: ROW_STATUS.ERROR, error: rowError(e, row) });
            return 0;
        }
    };

    /**
     * Several files: INTAKE_UPLOAD_BATCH, ONE code bound to every file's
     * facts. The server opens every batch or none: a refusal naming a file
     * (details.fileIndex) is said on that row and the others wait, ready; any
     * other refusal is said on every row. Once the tickets are issued each
     * file's bytes go on its own form, a row at a time.
     */
    const sendMany = async (rows) => {
        rows.forEach((row) => patchRow(row.key, { status: ROW_STATUS.SENDING, error: null }));
        const prepared = [];
        for (const row of rows) {
            const one = await prepareRow(row);
            if (one.error) {
                rows.forEach((r) => patchRow(r.key, r === row ? { status: ROW_STATUS.ERROR, error: one.error } : { status: ROW_STATUS.READY }));
                return 0;
            }
            prepared.push(one);
        }
        const files = prepared.map((p) => p.params);
        let uploads;
        try {
            ({ uploads } = await withStepUp('INTAKE_UPLOAD_BATCH', { files }, (stepUp) => api.intake.uploadBatch({ files, stepUp })));
        } catch (e) {
            if (e instanceof StepUpCancelled) {
                rows.forEach((row) => patchRow(row.key, { status: ROW_STATUS.READY }));
                return 0;
            }
            const named = rows[e?.details?.fileIndex];
            rows.forEach((row) => patchRow(row.key, named && row !== named
                ? { status: ROW_STATUS.READY }
                : { status: ROW_STATUS.ERROR, error: rowError(e, row) }));
            return 0;
        }
        let sent = 0;
        for (const [i, row] of rows.entries()) {
            if (await postRow(row, prepared[i], uploads[i])) sent += 1;
        }
        return sent;
    };

    /**
     * Proceed: every row still to send - one file on its own code, several on
     * one code for them all. A refusal is said on its row; closing the code
     * dialog sends nothing and leaves every row listed.
     */
    const send = async () => {
        const queue = toSend(stagedRef.current);
        if (!listSummary(queue, colomboToday()).canProceed) return;
        setSending(true);
        let sent = 0;
        try {
            sent = queue.length === 1 ? await sendOne(queue[0]) : await sendMany(queue);
        } finally {
            setSending(false);
        }
        if (sent) message.success(sent === 1 ? '1 file uploaded.' : sent + ' files uploaded.');
    };

    const download = async (batch) => {
        setDownloadingId(batch.batchId);
        try {
            const stored = await api.intake.file(batch.batchId);
            const bytes = await downloadedBytes(stored, batch.sha256);
            // Saved under a name of our making, whatever the server or the uploader said (C-32).
            downloadCsv(downloadName(stored.fileName ?? batch.fileName), bytes);
            message.success('Successfully Downloaded');
        } catch (e) {
            message.error('Failed to download ' + batch.fileName + ': ' + e.message);
        } finally {
            setDownloadingId(null);
        }
    };

    /** A class and kind's header row, saved as a CSV file to fill in (templates.js). */
    const downloadTemplate = (cls, templateKind) => {
        const template = templateFor(cls, templateKind);
        if (!template) return;
        downloadCsv(template.fileName, template.text);
        message.success(template.fileName + ' downloaded: fill it in and upload it.');
    };
    const templateMenu = {
        // A submenu for each File category group (templates.js templateGroups), so the list stays short.
        items: templateGroups(canInitial, fundIds).map((g) => ({
            key: 'group:' + g.label,
            label: g.label,
            children: g.choices.map((t) => ({ key: t.key, label: t.label }))
        })),
        onClick: ({ key }) => {
            const [cls, templateKind = null] = key.split(':');
            downloadTemplate(cls, templateKind);
        }
    };

    /** A column that sorts and searches as text, its matches highlighted. */
    const searchable = (dataIndex, title) => ({ dataIndex, key: dataIndex, sorter: byText(dataIndex), ...textSearch(dataIndex, title) });
    const fileNameSearch = searchable('fileName', 'File Name');
    const columns = [
        // Stable widths keep each sortable/filterable header readable. Narrow
        // viewports scroll horizontally instead of squeezing controls together.
        { title: 'File Name', width: 240, ...fileNameSearch, render: (v, b) => <Text strong style={{ wordBreak: 'break-all' }}>{fileNameSearch.render(v, b)}</Text> },
        {
            title: 'File Category',
            dataIndex: 'categoryLabel',
            key: 'categoryLabel',
            width: 155,
            sorter: byText('categoryLabel'),
            filters: filterOptions(listRows, (b) => b.fileClass, (b) => b.categoryLabel),
            filterSearch: true,
            onFilter: (value, b) => b.fileClass === value
        },
        { title: 'Upload Type', width: 150, ...searchable('kindLabel', 'Upload Type') },
        {
            title: 'Effective Date',
            dataIndex: 'businessDate',
            key: 'businessDate',
            width: 145,
            sorter: byText('businessDate'),
            ...dateRangeSearch('businessDate')
        },
        { title: 'Rows', width: 90, className: 'uploads-rows-cell', ...searchable('rowsText', 'Rows'), sorter: byNumber('rowCount') },
        {
            title: 'Uploaded Date',
            dataIndex: 'uploadedAt',
            key: 'uploadedAt',
            width: 175,
            sorter: byText('uploadedSortKey'),
            ...dateTimeRangeSearch('uploadedSortKey')
        },
        { title: 'Uploaded By', width: 145, ...searchable('uploadedByLabel', 'Uploaded By') },
        {
            title: 'Status',
            dataIndex: 'state',
            key: 'state',
            width: 135,
            sorter: byText('state'),
            // The filter lists each state by the name its tag shows, never the raw code.
            filters: filterOptions(listRows, (b) => b.state, (b) => statusLabel(STATUS_KIND.UPLOAD_BATCH, b.state)),
            onFilter: (value, b) => b.state === value,
            // The tag says the state in words and explains it on hover (status/statusHelp.js);
            // a long name still wraps inside its tag rather than widen the column.
            render: (v) => <StatusTag kind={STATUS_KIND.UPLOAD_BATCH} code={v} color={STATE_COLOUR[v]} style={{ whiteSpace: 'normal' }} />
        },
        {
            title: '',
            key: 'approval',
            width: 190,
            // Right-aligned, so the buttons sit beside the row's menu as one group of actions.
            className: 'uploads-decide-cell',
            // A VALIDATED file this person may decide: Approve and Reject open the drawer, where the decision is made.
            onCell: () => ({ onClick: (e) => e.stopPropagation() }),
            render: (_, b) => {
                // The uploader may not decide their own file (the server refuses them): they read who it waits for.
                const own = Boolean(me) && String(b.uploadedBy).toLowerCase() === String(me).toLowerCase();
                const waiting = b.state === 'VALIDATED' && !own;
                const showApprove = waiting && mayDecide(b, { canAccept, canInitial });
                const showReject = waiting && mayDecide(b, { canAccept: canReject, canInitial });
                if (!showApprove && !showReject) {
                    return <Text type="secondary" className="uploads-row-note">{rowNote(b, { own, canAccept, canReject, canInitial })}</Text>;
                }
                return (
                    <Space size={6} className="uploads-decide">
                        {showApprove && (
                            <Button size="small" className="uploads-approve" icon={<ExportOutlined />} iconPlacement="end"
                                onClick={() => setOpenBatchId(b.batchId)} aria-label={'Approve ' + b.fileName}>
                                Approve
                            </Button>
                        )}
                        {showReject && (
                            <Button size="small" className="uploads-reject" icon={<ExportOutlined />} iconPlacement="end"
                                onClick={() => setOpenBatchId(b.batchId)} aria-label={'Reject ' + b.fileName}>
                                Reject
                            </Button>
                        )}
                    </Space>
                );
            }
        },
        {
            title: '',
            key: 'actions',
            width: 48,
            // The row opens the file's rows and findings; its menu does not.
            onCell: () => ({ onClick: (e) => e.stopPropagation() }),
            render: (_, b) => {
                const why = downloadNotice(b);
                const items = [{
                    key: 'download',
                    icon: <DownloadOutlined />,
                    disabled: Boolean(why),
                    label: why
                        ? <span>Download<br /><Text type="secondary" style={{ fontSize: 11.5 }}>{why}</Text></span>
                        : 'Download'
                }];
                return (
                    <Dropdown trigger={['click']} menu={{ items, onClick: () => download(b) }}>
                        <Button type="text" icon={<EllipsisOutlined />} loading={downloadingId === b.batchId} aria-label={'Actions for ' + b.fileName} />
                    </Dropdown>
                );
            }
        }
    ];

    // The list under the drop zone: a row per file chosen, editable until it is sent.
    const today = colomboToday();
    const summary = listSummary(staged, today);
    // IP-184: a fund's PAYOUT account (its redemption payments' statement) is offered when it is not also a collection account.
    const accountOptions = accounts.map((a) => ({
        value: a.accountId, label: a.fundId + ' · ' + a.bankName + ' · ' + a.accountNumber + (a.purpose === 'PAYOUT' ? ' · Payout' : '')
    }));
    const locked = (r) => !canUpload || sending || r.status === ROW_STATUS.DONE || r.status === ROW_STATUS.SENDING;
    /** The words under the field that still stops a row being sent. */
    const problemUnder = (r, field) => {
        if (locked(r)) return null;
        const problem = rowProblem(r, today);
        return problem?.field === field ? <div className="uploads-cell-problem">{problem.text}</div> : null;
    };
    const errorStatus = (r, field) => (!locked(r) && rowProblem(r, today)?.field === field ? 'error' : undefined);
    const stagedColumns = [
        {
            title: 'File',
            key: 'file',
            width: 240,
            render: (_, r) => (
                <div>
                    <Text strong className="uploads-staged-name">{r.file.name}</Text>
                    <div><Text type="secondary" className="uploads-staged-size">{sizeText(r.file.size)}</Text></div>
                </div>
            )
        },
        {
            title: 'File Category',
            key: 'fileClass',
            width: 175,
            render: (_, r) => (
                <>
                    <Select value={r.fileClass ?? undefined} placeholder="Choose" options={categoryOptions} disabled={locked(r)}
                        status={errorStatus(r, 'fileClass')} onChange={(v) => editRow(r.key, { fileClass: v })}
                        style={{ width: '100%' }} aria-label={'File category of ' + r.file.name} />
                    {problemUnder(r, 'fileClass')}
                </>
            )
        },
        {
            title: 'Upload Type',
            key: 'loadKind',
            width: 175,
            render: (_, r) => (takesLoadKind(r.fileClass) ? (
                <>
                    <Segmented options={kindOptions(canInitial, r.fileClass)} size="small" value={r.loadKind} disabled={locked(r)}
                        onChange={(v) => editRow(r.key, { loadKind: v })} />
                    {problemUnder(r, 'loadKind')}
                </>
            ) : '—')
        },
        {
            title: 'Effective Date',
            key: 'businessDate',
            width: 160,
            render: (_, r) => {
                const rule = dateRule(r.fileClass, r.loadKind);
                return (
                    <>
                        <DatePicker
                            value={r.businessDate ? dayjs(r.businessDate) : null}
                            format="YYYY-MM-DD"
                            allowClear={false}
                            disabled={locked(r) || !r.fileClass || rule.rule === 'TODAY'}
                            status={errorStatus(r, 'businessDate')}
                            disabledDate={(d) => !dateAllowed(rule.rule, d.format('YYYY-MM-DD'), colomboToday())}
                            onChange={(d) => d && editRow(r.key, { businessDate: d.format('YYYY-MM-DD') })}
                            style={{ width: '100%' }}
                            aria-label={rule.label + ' of ' + r.file.name}
                        />
                        {problemUnder(r, 'businessDate')}
                        {!problemUnder(r, 'businessDate') && r.fileClass && rule.rule !== 'NOT_FUTURE' && (
                            <div className="uploads-cell-hint">{rule.rule === 'TODAY' ? 'Daily: today only' : 'As-at: before today'}</div>
                        )}
                    </>
                );
            }
        },
        {
            title: 'Bank Account',
            key: 'accountId',
            width: 230,
            render: (_, r) => (r.fileClass === 'BANK_STATEMENT' ? (
                <>
                    <Select value={r.accountId ?? undefined} placeholder="The account the statement is for" options={accountOptions}
                        loading={accountsLoading} disabled={locked(r)} status={errorStatus(r, 'accountId')}
                        onChange={(v) => editRow(r.key, { accountId: v })} style={{ width: '100%' }} aria-label={'Bank account of ' + r.file.name} />
                    {problemUnder(r, 'accountId')}
                </>
            ) : '—')
        },
        {
            title: 'Progress',
            key: 'status',
            width: 190,
            render: (_, r) => {
                if (r.status === ROW_STATUS.SENDING) return <span className="uploads-progress"><LoadingOutlined /> Sending</span>;
                if (r.status === ROW_STATUS.DONE) {
                    return <span className="uploads-progress uploads-progress-done"><CheckCircleFilled aria-label="Uploaded" /> Uploaded</span>;
                }
                if (r.status === ROW_STATUS.ERROR) {
                    return (
                        <span className="uploads-progress uploads-progress-error">
                            <CloseCircleFilled aria-label="Failed" /> <span>{r.error}</span>
                        </span>
                    );
                }
                return null;
            }
        },
        {
            title: '',
            key: 'remove',
            width: 48,
            render: (_, r) => (
                <Button type="text" icon={<DeleteOutlined />} disabled={sending || r.status === ROW_STATUS.SENDING}
                    onClick={() => removeRow(r.key)} aria-label={'Remove ' + r.file.name} />
            )
        }
    ];
    const footerText = sending
        ? 'Sending…'
        : summary.pending === 0
            ? (summary.done === 1 ? 'The file was uploaded.' : 'All ' + summary.done + ' files were uploaded.') + ' They wait in Uploaded files for another person to accept them.'
            : summary.problems
                ? (summary.pending - summary.problems) + ' of ' + summary.pending + ' ready. Fix ' + summary.problems + (summary.problems === 1 ? ' row' : ' rows') + ' to continue.'
                : summary.pending === 1 ? '1 file ready.' : summary.pending + ' files ready. One code sends them all.';

    return (
        <div className="workspace uploads-workspace">
            <div className="workspace-body uploads-page">
                <section className="panel uploads-panel uploads-upload-panel">
                    <h3 className="panel-title">Upload CSV files</h3>
                    <div className="uploads-intro">
                        <div className="uploads-info-banner">
                            <ActionIcon name="info" />
                            <span>
                               Upload NAV values, bank statements, and Core data such as calendar, unit price, holdings, and transaction files as CSV files, up to 4 MB each. Uploading, accepting, or rejecting a file requires a code from your authenticator app. Each file is scanned for malware and validated, and must be accepted by another user before it is processed.
                            </span>
                        </div>
                        <Dropdown trigger={['click']} menu={templateMenu}>
                            <Button icon={<DownloadOutlined />}>Download template</Button>
                        </Dropdown>
                    </div>
                    {!canUpload && <Alert type="info" showIcon message="Your roles let you see uploads but not add them." className="auth-alert" />}
                    <Dragger
                        accept=".csv,text/csv"
                        multiple
                        showUploadList={false}
                        disabled={!canUpload || sending}
                        className={staged.length ? 'uploads-dragger-compact' : undefined}
                        beforeUpload={(chosen, chosenList) => {
                            // Called once per file of the same pick or drop: the first call stages them all.
                            if (chosen.uid === chosenList[0]?.uid) choose(chosenList);
                            return false;
                        }}
                    >
                        <p className="lfoc-upload-drag-icon"><InboxOutlined style={{ color: 'var(--brand-primary)' }} /></p>
                        <p className="lfoc-upload-text">{staged.length ? 'Click or drag more CSV files here' : 'Click or drag CSV files here'}</p>
                        <p className="lfoc-upload-hint">
                            Up to {MAX_STAGED} files at a time. Nothing is sent until you press Proceed and enter your code.
                        </p>
                    </Dragger>
                    {skipped.length > 0 && (
                        <Alert type="warning" showIcon closable onClose={() => setSkipped([])} className="auth-alert uploads-skipped"
                            message={skipped.length === 1 ? 'A file was not added' : skipped.length + ' files were not added'}
                            description={skipped.map((s) => <div key={s.fileName}><Text strong>{s.fileName}</Text>: {s.reason}.</div>)} />
                    )}
                    {staged.length > 0 && (
                        <div className="uploads-staged">
                            <div className="uploads-staged-toolbar">
                                <Space align="center">
                                    <h4 className="panel-title">Files to upload</h4>
                                    <Tag>{staged.length === 1 ? '1 file' : staged.length + ' files'}</Tag>
                                </Space>
                                <Space align="center" wrap>
                                    <label className="uploads-date-all">
                                        Set date for all{' '}
                                        <DatePicker
                                            format="YYYY-MM-DD"
                                            allowClear={false}
                                            disabled={!canUpload || sending || summary.pending === 0}
                                            disabledDate={(d) => d.format('YYYY-MM-DD') > colomboToday()}
                                            onChange={(d) => d && setStaged((current) => dateForAll(current, d.format('YYYY-MM-DD'), settleOpts()))}
                                            aria-label="Date for all files"
                                        />
                                    </label>
                                    <Button type="link" disabled={sending} onClick={() => setStaged([])}>Clear all</Button>
                                </Space>
                            </div>
                            {hasStatement && accountsError && (
                                <Alert type="error" showIcon className="auth-alert" message={(
                                    <span>
                                        The bank accounts could not be loaded: {accountsError}{' '}
                                        <Button type="link" size="small" onClick={() => setAccountsAttempt((n) => n + 1)}>Try again</Button>
                                    </span>
                                )} />
                            )}
                            {fundsError && (
                                <Alert type="error" showIcon className="auth-alert" message={(
                                    <span>
                                        The funds could not be loaded, so the security holdings and fund facts categories are not offered: {fundsError}{' '}
                                        <Button type="link" size="small" onClick={() => setFundsAttempt((n) => n + 1)}>Try again</Button>
                                    </span>
                                )} />
                            )}
                            <Table rowKey="key" size="small" columns={stagedColumns} dataSource={staged} pagination={false}
                                scroll={{ x: 1218 }} rowClassName={(r) => (r.status === ROW_STATUS.ERROR ? 'uploads-staged-error' : '')} />
                            <div className="uploads-staged-footer">
                                <Text type={summary.problems && !sending ? 'warning' : 'secondary'}>{footerText}</Text>
                                <Space>
                                    {summary.done > 0 && !sending && (
                                        <Button onClick={() => setStaged((current) => current.filter((r) => r.status !== ROW_STATUS.DONE))}>
                                            {summary.pending === 0 ? 'Clear list' : 'Clear uploaded'}
                                        </Button>
                                    )}
                                    <Button type="primary" icon={<SafetyOutlined />} loading={sending} disabled={!canUpload || !summary.canProceed}
                                        onClick={send}>
                                        {'Proceed · ' + (summary.pending === 1 ? '1 file' : summary.pending + ' files')}
                                    </Button>
                                </Space>
                            </div>
                        </div>
                    )}
                </section>

                <section className="panel uploads-panel uploads-list-panel">
                    <h3 className="panel-title">Uploaded files</h3>
                    <div className="uploads-list-toolbar">
                        <div className="uploads-info-banner uploads-refresh-banner">
                            <ActionIcon name="info" />
                            <span>
                                Last refreshed {refreshedAt ? refreshedAt.toLocaleTimeString('en-GB', { timeZone: 'Asia/Colombo' }) : '—'}.
                            </span>
                        </div>
                        <Space wrap>
                            <Select value={kindFilter} onChange={setKindFilter} options={KIND_FILTERS} className="uploads-kind-filter" aria-label="Upload Type" />
                            <Button className="refresh-button" icon={<ActionIcon name="refresh" />} onClick={load} loading={loading}>Refresh</Button>
                        </Space>
                    </div>
                    {listError && <Alert type="error" showIcon message={listError} className="auth-alert" />}
                    <Tabs
                        className="workspace-tabs uploads-tabs"
                        items={[
                            {
                                key: 'files',
                                label: 'Uploaded Files',
                                children: (
                                    <>
                                        <Table
                                            className="uploads-files-table"
                                            rowKey="batchId"
                                            size="small"
                                            loading={loading}
                                            columns={columns}
                                            dataSource={loading && batches.length === 0 ? undefined : listRows}
                                            pagination={{ pageSize: 20, hideOnSinglePage: true }}
                                            tableLayout="fixed"
                                            scroll={{ x: 1430 }}
                                            locale={{ emptyText: 'No files uploaded yet' }}
                                            onRow={(batch) => ({ onClick: () => setOpenBatchId(batch.batchId), style: { cursor: 'pointer' } })}
                                        />
                                        {nextCursor && (
                                            <div className="uploads-rows-more">
                                                <Button size="small" onClick={loadMore} loading={loadingMore} disabled={loading}>Load more</Button>
                                                <Text type="secondary">
                                                    {' ' + batches.length + ' files shown - search, filters and sorting cover these'}
                                                </Text>
                                            </div>
                                        )}
                                    </>
                                )
                            },
                            { key: 'errors', label: 'Error logs', children: <ErrorLog rows={errorRows} loading={loading} /> }
                        ]}
                    />
                </section>
            </div>

            <BatchDrawer
                batchId={openBatchId}
                version={drawerVersion}
                canAccept={canAccept}
                canReject={canReject}
                canInitial={canInitial}
                me={me}
                withStepUp={withStepUp}
                onClose={() => setOpenBatchId(null)}
                onChanged={load}
            />
            {stepUpDialog}
        </div>
    );
};

/** The drawer's Findings: the first five, and how many more the rows below hold. */
function findingsDescription(summary) {
    const shown = summary.findings.slice(0, 5);
    const more = unshownFindings(summary, shown.length);
    return [...shown.map(findingLine), ...(more ? ['and ' + more + ' more in the rows below'] : [])].join(' · ');
}

/** The rows table's columns: the row as the parser read it, and what was found on it. */
const FINDING_COLUMNS = [
    { title: 'Row', dataIndex: 'rowNo', key: 'rowNo', width: 70 },
    { title: 'Data', dataIndex: 'data', key: 'data', render: (d) => <Text code>{JSON.stringify(d)}</Text> },
    {
        title: 'Findings',
        dataIndex: 'findings',
        key: 'findings',
        render: (list) => (list?.length
            ? list.map((f, i) => <div key={i}><Tag color={f.severity === 'ERROR' ? 'red' : 'gold'}>{f.severity}</Tag>{f.message}</div>)
            : <Text type="secondary">none</Text>)
    }
];

/**
 * One batch: its facts, its class's summary and warnings, its rows and their
 * findings - a page at a time, all of them or only those with findings - and,
 * for another person, accept or reject, each with a code bound to the batch
 * and the bytes shown here (its sha256; an accept's also its summarySha256).
 * An initial load is decided only by someone who also holds
 * core.intake.initial (uploadKinds.js mayDecide); an accepted daily holdings
 * file shows its comparison with the register (HoldingChecks).
 */
const BatchDrawer = ({ batchId, version, canAccept, canReject, canInitial, me, withStepUp, onClose, onChanged }) => {
    const { api } = useAccess();
    const { message } = App.useApp();
    const [detail, setDetail] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const [reloads, setReloads] = useState(0);
    // "Rows with findings only" (?withFindings=true), and the rows read so far with the cursor of the next page.
    const [withFindings, setWithFindings] = useState(false);
    const [rows, setRows] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [rowsLoading, setRowsLoading] = useState(false);
    const [rejectForm] = Form.useForm();

    useEffect(() => {
        setError(null);
        setWithFindings(false);
    }, [batchId]);

    // The batch and the first page of its rows; a later read wins.
    useEffect(() => {
        if (!batchId) return undefined;
        let live = true;
        setDetail(null);
        setRows([]);
        setNextCursor(null);
        setRowsLoading(true);
        api.intake.batch(batchId, rowsQuery({ withFindings }))
            .then((answer) => {
                if (!live) return;
                setDetail(answer);
                setRows(answer.rows ?? []);
                setNextCursor(answer.nextCursor ?? null);
            })
            .catch((e) => { if (live) setError(e.message); })
            .finally(() => { if (live) setRowsLoading(false); });
        return () => { live = false; };
    }, [api, batchId, version, reloads, withFindings]);

    /** Load more: the next page of the same rows, under those shown. */
    const more = async () => {
        setRowsLoading(true);
        try {
            const page = await api.intake.batch(batchId, rowsQuery({ withFindings, cursor: nextCursor }));
            setRows((current) => appendRows(current, page.rows));
            setNextCursor(page.nextCursor ?? null);
        } catch (e) {
            setError(e.message);
        } finally {
            setRowsLoading(false);
        }
    };

    const batch = detail?.batch;
    const ownUpload = batch && me && String(batch.uploadedBy).toLowerCase() === String(me).toLowerCase();
    const waiting = batch?.state === 'VALIDATED';
    const mayAccept = waiting && mayDecide(batch, { canAccept, canInitial });
    const mayReject = waiting && mayDecide(batch, { canAccept: canReject, canInitial });
    const decidable = mayAccept || mayReject;
    // An initial load waits for someone who holds core.intake.initial as well as accept's claim.
    const initialOnly = waiting && batch?.loadKind === 'INITIAL' && (canAccept || canReject) && !canInitial;
    const notice = batch ? stateNotice(batch) : null;
    const withheld = withheldText(detail?.rowsWithheld);

    // A refusal that means the batch moved on (expired, decided by someone
    // else, changed): say so, and show it as it now is - here and in the list.
    const refused = (e) => {
        if (e instanceof StepUpCancelled) return;
        setError(decisionErrorMessage(e));
        if (isStaleDecision(e)) {
            setRejecting(false);
            setReloads((n) => n + 1);
            onChanged();
        }
    };

    // The reason dialog opens with no refusal of an earlier attempt in it.
    const openReject = () => {
        setError(null);
        setRejecting(true);
    };

    const accept = async () => {
        setBusy(true);
        setError(null);
        try {
            const bound = { sha256: batch.sha256, summarySha256: batch.summarySha256 };
            await withStepUp('INTAKE_ACCEPT', { batchId: batch.batchId, ...bound },
                (stepUp) => api.intake.accept(batch.batchId, { ...bound, stepUp }));
            message.success(batch.fileName + ' accepted.');
            onChanged();
            onClose();
        } catch (e) {
            refused(e);
        } finally {
            setBusy(false);
        }
    };

    // From the reason dialog: the step-up dialog opens above it (notes.js STEP_UP_Z_INDEX). A
    // refusal - before the code (STEP_UP_LOCKED, STEP_UP_RATE_LIMITED, the network) or after it -
    // is said IN the reason dialog, which stays open over the drawer's own alert; one that means
    // the batch moved on closes the dialog (refused, above) and is said in the drawer.
    const reject = async ({ reason }) => {
        setBusy(true);
        setError(null);
        try {
            await withStepUp('INTAKE_REJECT', { batchId: batch.batchId, sha256: batch.sha256, reason },
                (stepUp) => api.intake.reject(batch.batchId, { sha256: batch.sha256, reason, stepUp }));
            message.info(batch.fileName + ' rejected.');
            setRejecting(false);
            onChanged();
            onClose();
        } catch (e) {
            refused(e);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Drawer open={Boolean(batchId)} onClose={onClose} width={900} title={batch?.fileName ?? 'File'} rootClassName="lfoc-styles-wrapper"
            extra={decidable && (
                <Space>
                    {mayReject && <Button danger onClick={openReject} disabled={busy}>Reject</Button>}
                    {mayAccept && <Button type="primary" onClick={accept} loading={busy} disabled={ownUpload}>Accept</Button>}
                </Space>
            )}
        >
            {error && !rejecting && <Alert type="error" showIcon message={error} className="auth-alert" />}
            {decidable && ownUpload && (
                <Alert type="info" showIcon className="auth-alert" message="You uploaded this file, so another person must accept it." />
            )}
            {initialOnly && (
                <Alert type="info" showIcon className="auth-alert"
                    message="An initial load is accepted or rejected only by someone who also has the Initialise the Core from files permission." />
            )}
            {batch && (
                <>
                    <p>
                        <StatusTag kind={STATUS_KIND.UPLOAD_BATCH} code={batch.state} color={STATE_COLOUR[batch.state]} /> {classLabelOf(batch)}
                        {batch.loadKind === 'INITIAL' ? ' as at ' : ' for '}{batch.businessDate}
                        {' · '}uploaded {at(batch.createdAt)}
                    </p>
                    <p><Text type="secondary">SHA-256 </Text><Text code copyable>{batch.sha256}</Text></p>
                    {notice && <Alert type={notice.type} showIcon className="auth-alert" message={notice.title} description={notice.text} />}
                    {(batch.state === 'QUARANTINED' || batch.state === 'SCAN_FAILED') && batch.summary?.findings?.length > 0 && (
                        <Alert type="warning" showIcon className="auth-alert" message="Findings"
                            description={findingsDescription(batch.summary)} />
                    )}
                    <ClassSummary summary={batch.summary} />
                    <WarningsBlock summary={batch.summary} />
                    {showsHoldingChecks(batch, { rowsWithheld: detail.rowsWithheld }) && <HoldingChecks batchId={batch.batchId} />}
                    {withheld ? (
                        <Alert type="info" showIcon className="auth-alert" message={withheld} />
                    ) : (
                        <div className="uploads-rows">
                            <div className="uploads-rows-toolbar">
                                <h4 className="panel-title">Rows</h4>
                                <label className="uploads-findings-only">
                                    <Switch size="small" checked={withFindings} onChange={setWithFindings} disabled={rowsLoading} />
                                    {' '}Rows with findings only
                                </label>
                            </div>
                            <Table rowKey="rowNo" size="small" loading={rowsLoading} columns={FINDING_COLUMNS} dataSource={rows}
                                pagination={{ pageSize: 50, hideOnSinglePage: true }}
                                locale={{ emptyText: withFindings ? 'No row has a finding' : 'No rows' }} />
                            {nextCursor && (
                                <div className="uploads-rows-more">
                                    <Button size="small" onClick={more} loading={rowsLoading}>Load more</Button>
                                    <Text type="secondary">{' ' + rows.length + ' rows shown'}</Text>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
            <Modal open={rejecting} title="Reject this file?" onCancel={() => setRejecting(false)} onOk={() => rejectForm.submit()}
                okText="Reject" okButtonProps={{ danger: true, loading: busy }} rootClassName="lfoc-styles-wrapper" destroyOnHidden>
                {error && <Alert type="error" showIcon message={error} className="auth-alert" />}
                <Form form={rejectForm} layout="vertical" onFinish={reject}>
                    <Form.Item name="reason" label="Reason" rules={[{ required: true, whitespace: true, message: 'Say why it is rejected' }]}>
                        <Input.TextArea rows={3} maxLength={1000} />
                    </Form.Item>
                </Form>
            </Modal>
        </Drawer>
    );
};

export default UploadsTab;
